import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { GridColDef } from '@mui/x-data-grid-pro';
import { LOCKED_FIELD } from '../useColumnLayout';

interface Props {
  open: boolean;
  onClose: () => void;
  columns: GridColDef[];
  orderedFields: string[];
  hiddenFields: Set<string>;
  pinnedFields: Set<string>;
  onToggle: (field: string) => void;
  onTogglePin: (field: string) => void;
  onReorder: (from: number, to: number) => void;
  onReset: () => void;
}

// Show/hide, pin and reorder in one place. Drag-to-reorder uses
// react-beautiful-dnd, matching AdLibrary's ColumnPicker so the two column
// managers in the app behave the same way.
//
// Settings persist locally per level: a personal view preference, deliberately
// not part of the URL the way the filters are.
export default function ColumnSettings({
  open,
  onClose,
  columns,
  orderedFields,
  hiddenFields,
  pinnedFields,
  onToggle,
  onTogglePin,
  onReorder,
  onReset,
}: Props) {
  const labelFor = (field: string) => {
    const column = columns.find(item => item.field === field);
    return column?.headerName || field;
  };

  // The locked identity column is rendered outside the drop zone, so indices
  // here line up with the hook's movable list.
  const movableFields = orderedFields.filter(field => field !== LOCKED_FIELD);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    onReorder(result.source.index, result.destination.index);
  };

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    px: 1.5,
    py: 0.25,
    backgroundColor: 'background.paper',
  } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 18 }}>Columns</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: '#64748B', px: 2, py: 1, display: 'block' }}
        >
          Drag to reorder, tick to show, pin to keep in view when scrolling
          sideways. Saved on this browser.
        </Typography>

        <Box sx={{ ...rowSx, opacity: 0.7 }}>
          <Box sx={{ display: 'flex', width: 24, justifyContent: 'center' }}>
            <Tooltip title="Always first and pinned — every row is read from this column">
              <LockOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
            </Tooltip>
          </Box>
          <Checkbox size="small" checked disabled />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {labelFor(LOCKED_FIELD)}
          </Typography>
        </Box>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="creativePerformanceColumns">
            {provided => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ pb: 1 }}
              >
                {movableFields.map((field, index) => (
                  <Draggable key={field} draggableId={field} index={index}>
                    {draggable => (
                      <Box
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                        sx={rowSx}
                      >
                        <Box
                          {...draggable.dragHandleProps}
                          sx={{
                            display: 'flex',
                            width: 24,
                            justifyContent: 'center',
                            color: 'text.disabled',
                            cursor: 'grab',
                          }}
                        >
                          <DragIndicatorIcon sx={{ fontSize: 18 }} />
                        </Box>

                        <Checkbox
                          size="small"
                          checked={!hiddenFields.has(field)}
                          onChange={() => onToggle(field)}
                          inputProps={{ 'aria-label': `Show ${labelFor(field)}` }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            color: hiddenFields.has(field) ? '#94A3B8' : 'inherit',
                          }}
                        >
                          {labelFor(field)}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => onTogglePin(field)}
                          aria-label={`${
                            pinnedFields.has(field) ? 'Unpin' : 'Pin'
                          } ${labelFor(field)}`}
                        >
                          {pinnedFields.has(field) ? (
                            <PushPinIcon sx={{ fontSize: 16, color: '#6366F1' }} />
                          ) : (
                            <PushPinOutlinedIcon
                              sx={{ fontSize: 16, color: '#94A3B8' }}
                            />
                          )}
                        </IconButton>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>

      <DialogActions>
        <Button size="small" onClick={onReset}>
          Reset to default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
